import React, { useMemo, useState } from "react";
import { Mafs, Coordinates, Text, Theme, Plot, Line, MovablePoint, Polygon } from "mafs";

/**
 * Inequality Region (feedback visualisation)
 *
 * Flagship upgrades:
 * - Constrained viewBox auto-fit (safe defaults)
 * - Test point interaction + per-constraint checks
 * - Uses Mafs Plot.Inequality for y ? f(x) shading
 * - Vertical half-planes handled via Polygon fill
 * - Inclusive vs strict boundary styling (dashed for strict, solid for inclusive)
 * - Guaranteed interesting preset
 *
 * Config shape (typical):
 * {
 *   regions: [
 *     { a: 1, b: 0, c: 0, symbol: ">=", label: "x ≥ 0", color: "blue" },     // ax + by ? c
 *     { a: 0, b: 1, c: 0, symbol: ">=", label: "y ≥ 0", color: "blue" },
 *     { a: 1, b: 1, c: 6, symbol: "<=", label: "x + y ≤ 6", color: "blue" }
 *   ],
 *   testPoint?: { x: number, y: number },
 *   showLegend?: boolean,
 *   showWorkings?: boolean
 * }
 */
export const InequalityRegion = ({ config }) => {
  const safe = (n, fallback) => (Number.isFinite(n) ? n : fallback);

  // FIX: define clamp before any hook that uses it (avoids TDZ crash)
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  const cfg = config || {};

  // A guaranteed bounded preset (triangle in quadrant I).
  const presetRegions = useMemo(
      () => [
        { a: 1, b: 0, c: 0, symbol: ">=", label: "x ≥ 0", color: "blue" },
        { a: 0, b: 1, c: 0, symbol: ">=", label: "y ≥ 0", color: "blue" },
        { a: 1, b: 1, c: 6, symbol: "<=", label: "x + y ≤ 6", color: "blue" },
      ],
      []
  );

  const regionsRaw = Array.isArray(cfg.regions) && cfg.regions.length ? cfg.regions : presetRegions;

  const showLegend = cfg.showLegend !== false;
  const showWorkings = cfg.showWorkings !== false;

  const initialPoint = useMemo(() => {
    const x = safe(cfg?.testPoint?.x, 1);
    const y = safe(cfg?.testPoint?.y, 1);
    return [x, y];
  }, [cfg?.testPoint?.x, cfg?.testPoint?.y]);

  const [testPoint, setTestPoint] = useState(initialPoint);

  // --- Symbol helpers ---
  const symStr = (s) => (s ?? ">=").toString().trim();
  const isStrict = (s) => s === ">" || s === "<";

  const compare = (lhs, symbol, rhs) => {
    if (!Number.isFinite(lhs) || !Number.isFinite(rhs)) return false;
    switch (symbol) {
      case ">":
        return lhs > rhs;
      case ">=":
        return lhs >= rhs;
      case "<":
        return lhs < rhs;
      case "<=":
        return lhs <= rhs;
      default:
        return false;
    }
  };

  const fmt = (n) => {
    if (!Number.isFinite(n)) return "—";
    if (Math.abs(n) >= 100) return n.toFixed(0);
    if (Math.abs(n) >= 10) return n.toFixed(1);
    return n.toFixed(2);
  };

  // --- Normalize regions into render + check structures ---
  const regions = useMemo(() => {
    return regionsRaw
        .map((r, idx) => {
          const label = (r?.label ?? `Constraint ${idx + 1}`).toString();
          const color = (r?.color ?? "blue").toString();

          const symbol = symStr(r?.symbol);
          const a = r?.a;
          const b = r?.b;
          const c = r?.c;

          const hasStandard = Number.isFinite(a) || Number.isFinite(b) || Number.isFinite(c);

          if (hasStandard) {
            const aa = safe(a, 0);
            const bb = safe(b, 0);
            const cc = safe(c, 0);

            // vertical: b == 0 => a*x ? c
            if (Math.abs(bb) < 1e-12) {
              // Guard against degenerate
              const aSafe = Math.abs(aa) < 1e-12 ? 1 : aa;
              const x0 = cc / aSafe;

              // If a is negative, dividing flips the inequality.
              const xSymbol = aSafe < 0 ? flip(symbol) : symbol;

              return {
                kind: "x",
                label,
                color,
                symbol: xSymbol,
                x0,
                // check with original standard form
                check: (x, y) => compare(aa * x + bb * y, symbol, cc),
                explain: (x, y) => explainStandard(aa, bb, cc, symbol, x, y),
              };
            }

            // Solve for y: y (symbolY) f(x)
            const ySymbol = bb < 0 ? flip(symbol) : symbol;
            const fn = (x) => (cc - aa * x) / bb;

            return {
              kind: "y",
              label,
              color,
              symbol: ySymbol,
              fn,
              boundary: fn,
              check: (x, y) => compare(aa * x + bb * y, symbol, cc),
              explain: (x, y) => explainStandard(aa, bb, cc, symbol, x, y),
            };
          }

          // Legacy: y (symbol) (m x + b)
          const m = safe(r?.slope, 1);
          const b0 = safe(r?.yIntercept, 0);
          const symbolLegacy = symStr(r?.symbol);

          const fn = (x) => m * x + b0;

          return {
            kind: "y",
            label,
            color,
            symbol: symbolLegacy,
            fn,
            boundary: fn,
            check: (x, y) => compare(y, symbolLegacy, fn(x)),
            explain: (x, y) => explainSlope(m, b0, symbolLegacy, x, y),
          };
        })
        .filter(Boolean);
  }, [regionsRaw]);

  function flip(symbol) {
    switch (symbol) {
      case ">":
        return "<";
      case ">=":
        return "<=";
      case "<":
        return ">";
      case "<=":
        return ">=";
      default:
        return symbol;
    }
  }

  function explainStandard(a, b, c, symbol, x, y) {
    const lhs = a * x + b * y;
    const ok = compare(lhs, symbol, c);
    const sign = ok ? "✓" : "✗";
    return `${fmt(a)}·${fmt(x)} + ${fmt(b)}·${fmt(y)} = ${fmt(lhs)}  ${symbol}  ${fmt(c)}  ${sign}`;
  }

  function explainSlope(m, b0, symbol, x, y) {
    const rhs = m * x + b0;
    const ok = compare(y, symbol, rhs);
    const sign = ok ? "✓" : "✗";
    return `y = ${fmt(y)}  ${symbol}  (${fmt(m)}·${fmt(x)} + ${fmt(b0)}) = ${fmt(rhs)}  ${sign}`;
  }

  const evaluations = useMemo(() => {
    const x = testPoint[0];
    const y = testPoint[1];
    return regions.map((r) => ({
      ok: r.check(x, y),
      explain: r.explain(x, y),
    }));
  }, [regions, testPoint]);

  const allOk = evaluations.every((e) => e.ok);

  const firstFailIdx = evaluations.findIndex((e) => !e.ok);

  // --- Auto-fit viewBox (safe, includes vertical boundaries) ---
  const viewBox = useMemo(() => {
    const base = { x: [-6, 6], y: [-6, 6] };
    if (!regions.length) return base;

    const xCandidates = [-8, -6, -4, 0, 4, 6, 8];
    const xs = [];

    for (const r of regions) {
      if (r.kind === "x" && Number.isFinite(r.x0)) xs.push(r.x0);
    }

    for (const x of xCandidates) xs.push(x);

    const ys = [];
    for (const r of regions) {
      if (r.kind === "y") {
        for (const x of xCandidates) {
          const y = r.fn(x);
          if (Number.isFinite(y)) ys.push(y);
        }
      }
    }

    // Include test point
    xs.push(testPoint[0]);
    ys.push(testPoint[1]);

    const minX = Math.min(...xs.filter(Number.isFinite));
    const maxX = Math.max(...xs.filter(Number.isFinite));
    const minY = Math.min(...ys.filter(Number.isFinite));
    const maxY = Math.max(...ys.filter(Number.isFinite));

    if (
        !Number.isFinite(minX) ||
        !Number.isFinite(maxX) ||
        !Number.isFinite(minY) ||
        !Number.isFinite(maxY)
    ) {
      return base;
    }

    const padX = Math.max(1.5, (maxX - minX) * 0.2);
    const padY = Math.max(1.5, (maxY - minY) * 0.2);

    const xLo = clamp(minX - padX, -12, 12);
    const xHi = clamp(maxX + padX, -12, 12);
    const yLo = clamp(minY - padY, -12, 12);
    const yHi = clamp(maxY + padY, -12, 12);

    // Avoid degenerate viewBox
    if (xHi - xLo < 4 || yHi - yLo < 4) return base;

    return { x: [xLo, xHi], y: [yLo, yHi] };
  }, [regions, testPoint]);

  const setValidPoint = () => {
    const candidates = [
      [0, 0],
      [1, 1],
      [2, 1],
      [1, 2],
      [3, 1],
      [2, 2],
      [4, 1],
      [1, 4],
    ];

    for (const p of candidates) {
      const ok = regions.every((r) => r.check(p[0], p[1]));
      if (ok) {
        setTestPoint(p);
        return;
      }
    }

    // fallback
    setTestPoint([1, 1]);
  };

  return (
      <div className="ch-inequality-region-widget">
        <div className="ch-inequality-region-header">
          <div className="ch-inequality-region-title">Inequality Region</div>
          <div className="ch-inequality-region-actions">
            <button className="ch-btn" onClick={() => setTestPoint(initialPoint)}>
              Reset
            </button>
            <button className="ch-btn" onClick={setValidPoint}>
              Find valid point
            </button>
          </div>
        </div>

        <div className="ch-inequality-region-stage">
          <Mafs viewBox={viewBox} pan={false}>
            <Coordinates.Cartesian subdivisions={1} />

            {/* Regions */}
            {regions.map((r, idx) => {
              const sym = r.symbol;
              const failing = idx === firstFailIdx;

              const boundaryStyle = isStrict(sym) ? "dashed" : "solid";
              const boundaryWeight = failing ? 3 : 2;

              if (r.kind === "y") {
                // FIX: Mafs expects operator map passed via y={...} (or x={...}), not spread props
                const opMap = { [sym]: r.fn };

                return (
                    <React.Fragment key={`r-${idx}`}>
                      <Plot.Inequality y={opMap} color={r.color} opacity={0.15} />
                      <Plot.OfX
                          y={r.boundary}
                          color={r.color}
                          weight={boundaryWeight}
                          style={boundaryStyle}
                      />
                    </React.Fragment>
                );
              }

              // Vertical half-plane: x (sym) x0
              const x0 = r.x0;
              const [xMin, xMax] = viewBox.x;
              const [yMin, yMax] = viewBox.y;

              const left = sym === "<" || sym === "<=";
              const poly = left
                  ? [
                    [xMin, yMin],
                    [x0, yMin],
                    [x0, yMax],
                    [xMin, yMax],
                  ]
                  : [
                    [x0, yMin],
                    [xMax, yMin],
                    [xMax, yMax],
                    [x0, yMax],
                  ];

              return (
                  <React.Fragment key={`r-${idx}`}>
                    <Polygon points={poly} color={r.color} fillOpacity={0.15} strokeOpacity={0} />
                    {/* FIX: use a real segment for the boundary */}
                    <Line.Segment
                        point1={[x0, yMin]}
                        point2={[x0, yMax]}
                        color={r.color}
                        weight={boundaryWeight}
                        style={boundaryStyle}
                    />
                  </React.Fragment>
              );
            })}

            {/* Test point (primary interaction) */}
            <MovablePoint
                point={testPoint}
                onMove={setTestPoint}
                color={allOk ? Theme.green : Theme.red}
            />
            <Text x={testPoint[0]} y={testPoint[1]} attach="ne" size={14}>
              Test point
            </Text>
          </Mafs>
        </div>

        {showLegend && (
            <div className="ch-inequality-region-legend">
              {regions.map((r, idx) => {
                const ok = evaluations[idx]?.ok;
                return (
                    <div
                        key={`leg-${idx}`}
                        className="ch-inequality-region-legend-row"
                        style={{ opacity: ok ? 1 : 0.9 }}
                    >
                <span
                    className="ch-swatch"
                    style={{
                      background: r.color,
                      border: isStrict(r.symbol) ? "2px dashed rgba(0,0,0,0.35)" : "2px solid rgba(0,0,0,0.35)",
                    }}
                />
                      <span className="ch-inequality-region-legend-label">{r.label}</span>
                      <span className="ch-inequality-region-legend-status" style={{ color: ok ? Theme.green : Theme.red }}>
                  {ok ? "✓" : "✗"}
                </span>
                    </div>
                );
              })}
            </div>
        )}

        {showWorkings && (
            <div className="ch-inequality-region-workings">
              <div className="ch-inequality-region-workings-title">Checks at ({fmt(testPoint[0])}, {fmt(testPoint[1])})</div>
              {evaluations.map((e, idx) => (
                  <div key={`w-${idx}`} className="ch-inequality-region-working-row" style={{ color: e.ok ? Theme.green : Theme.red }}>
                    {e.explain}
                  </div>
              ))}
            </div>
        )}
      </div>
  );
};
